/**
 * Usage Tracking for Analytics
 * Quản lý tracking và analytics cho admin dashboard
 */

import {
  getDatabase,
  ref,
  get,
  set,
} from "firebase/database";
import { getCurrentDateVN } from "./dailyUsageManager";

/**
 * Track khi user đạt giới hạn daily limit
 * @param {string} userId
 * @param {number} limit
 * @returns {Promise<void>}
 */
export const trackLimitReached = async (userId, limit) => {
  try {
    const db = getDatabase();
    const currentDate = getCurrentDateVN();
    const timestamp = Date.now();

    // Update analytics
    const analyticsRef = ref(
      db,
      `/analytics/daily/${currentDate}/limitReachedCount`
    );
    const snapshot = await get(analyticsRef);

    const currentCount = snapshot.exists() ? snapshot.val() : 0;
    await set(analyticsRef, currentCount + 1);

    // Log vào user history
    const userLimitLogRef = ref(
      db,
      `/users/${userId}/limitReachedLog/${currentDate}`
    );
    await set(userLimitLogRef, {
      timestamp,
      limit,
    });

    console.log(
      `📊 Tracked limit reached for user ${userId} on ${currentDate}`
    );
  } catch (error) {
    console.error("❌ Error tracking limit reached:", error);
    // Không throw error vì tracking không nên block main flow
  }
};

/**
 * Lấy thống kê tổng quan cho 1 ngày cụ thể
 * @param {string} date - Format YYYY-MM-DD
 * @returns {Promise<Object>}
 */
export const getDailyStats = async (date) => {
  try {
    const db = getDatabase();
    const analyticsRef = ref(db, `/analytics/daily/${date}`);
    const snapshot = await get(analyticsRef);

    if (!snapshot.exists()) {
      return {
        totalRequests: 0,
        uniqueUsers: 0,
        limitReachedCount: 0,
        averageUsagePerUser: 0,
      };
    }

    const data = snapshot.val();
    const averageUsagePerUser =
      data.uniqueUsers > 0
        ? (data.totalRequests / data.uniqueUsers).toFixed(2)
        : 0;

    return {
      totalRequests: data.totalRequests || 0,
      uniqueUsers: data.uniqueUsers || 0,
      limitReachedCount: data.limitReachedCount || 0,
      averageUsagePerUser: parseFloat(averageUsagePerUser),
      lastUpdated: data.lastUpdated || null,
    };
  } catch (error) {
    console.error("❌ Error getting daily stats:", error);
    return {
      totalRequests: 0,
      uniqueUsers: 0,
      limitReachedCount: 0,
      averageUsagePerUser: 0,
    };
  }
};

/**
 * Lấy top users sử dụng nhiều nhất trong 1 ngày
 * @param {string} date - Format YYYY-MM-DD
 * @param {number} limit - Số lượng top users cần lấy
 * @returns {Promise<Array>}
 */
export const getTopUsers = async (date, limit = 10) => {
  try {
    const db = getDatabase();

    // Lấy tất cả users có usage history trong ngày đó
    const usersRef = ref(db, `/users`);
    const snapshot = await get(usersRef);

    if (!snapshot.exists()) {
      return [];
    }

    const usersData = snapshot.val();
    const topUsers = [];

    // Duyệt qua tất cả users
    Object.keys(usersData).forEach((userId) => {
      const userData = usersData[userId];

      if (userData.usageHistory && userData.usageHistory[date]) {
        const usageData = userData.usageHistory[date];
        topUsers.push({
          userId,
          displayName: userData.profile?.displayName || "Unknown User",
          email: userData.profile?.email || "No email",
          count: usageData.count || 0,
          date,
        });
      }
    });

    // Sort by count descending và lấy top limit
    topUsers.sort((a, b) => b.count - a.count);
    return topUsers.slice(0, limit);
  } catch (error) {
    console.error("❌ Error getting top users:", error);
    return [];
  }
};

/**
 * Lấy usage stats cho 1 user cụ thể
 * @param {string} userId
 * @param {number} days - Số ngày cần lấy (default 30)
 * @returns {Promise<Object>}
 */
export const getUserUsageStats = async (userId, days = 30) => {
  try {
    const db = getDatabase();
    const userHistoryRef = ref(db, `/users/${userId}/usageHistory`);
    const snapshot = await get(userHistoryRef);

    if (!snapshot.exists()) {
      return {
        totalLifetimeUsage: 0,
        averageDailyUsage: 0,
        lastActiveDate: null,
        dailyBreakdown: [],
      };
    }

    const historyData = snapshot.val();
    const dates = Object.keys(historyData);

    // Sort dates descending
    dates.sort((a, b) => new Date(b) - new Date(a));

    // Lấy breakdown cho `days` ngày gần nhất
    const recentDates = dates.slice(0, days);
    const dailyBreakdown = recentDates.map((date) => ({
      date,
      count: historyData[date].count || 0,
    }));

    // Tính tổng lifetime usage
    const totalLifetimeUsage = dates.reduce((sum, date) => {
      return sum + (historyData[date].count || 0);
    }, 0);

    // Tính average daily usage
    const averageDailyUsage =
      dates.length > 0 ? (totalLifetimeUsage / dates.length).toFixed(2) : 0;

    return {
      totalLifetimeUsage,
      averageDailyUsage: parseFloat(averageDailyUsage),
      lastActiveDate: dates[0] || null,
      dailyBreakdown,
    };
  } catch (error) {
    console.error("❌ Error getting user usage stats:", error);
    return {
      totalLifetimeUsage: 0,
      averageDailyUsage: 0,
      lastActiveDate: null,
      dailyBreakdown: [],
    };
  }
};

/**
 * Lấy usage trend cho nhiều ngày (để vẽ chart)
 * @param {number} days - Số ngày cần lấy
 * @returns {Promise<Array>}
 */
export const getUsageTrend = async (days = 30) => {
  try {
    const db = getDatabase();
    const analyticsRef = ref(db, `/analytics/daily`);
    const snapshot = await get(analyticsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const dailyData = snapshot.val();
    const dates = Object.keys(dailyData);

    // Sort dates descending
    dates.sort((a, b) => new Date(b) - new Date(a));

    // Lấy `days` ngày gần nhất
    const recentDates = dates.slice(0, days);

    // Map to chart data format
    const trendData = recentDates.map((date) => ({
      date,
      totalRequests: dailyData[date].totalRequests || 0,
      uniqueUsers: dailyData[date].uniqueUsers || 0,
      limitReachedCount: dailyData[date].limitReachedCount || 0,
    }));

    // Sort lại theo date ascending để chart đúng
    trendData.sort((a, b) => new Date(a.date) - new Date(b.date));

    return trendData;
  } catch (error) {
    console.error("❌ Error getting usage trend:", error);
    return [];
  }
};

/**
 * Export analytics data ra CSV format
 * @param {string} startDate - Format YYYY-MM-DD
 * @param {string} endDate - Format YYYY-MM-DD
 * @returns {Promise<string>} CSV string
 */
export const exportAnalyticsData = async (startDate, endDate) => {
  try {
    const db = getDatabase();
    const analyticsRef = ref(db, `/analytics/daily`);
    const snapshot = await get(analyticsRef);

    if (!snapshot.exists()) {
      return "Date,Total Requests,Unique Users,Limit Reached Count\n";
    }

    const dailyData = snapshot.val();
    const dates = Object.keys(dailyData);

    // Filter dates trong range
    const filteredDates = dates.filter((date) => {
      return date >= startDate && date <= endDate;
    });

    // Sort dates
    filteredDates.sort((a, b) => new Date(a) - new Date(b));

    // Generate CSV
    let csv = "Date,Total Requests,Unique Users,Limit Reached Count\n";

    filteredDates.forEach((date) => {
      const data = dailyData[date];
      csv += `${date},${data.totalRequests || 0},${data.uniqueUsers || 0},${
        data.limitReachedCount || 0
      }\n`;
    });

    return csv;
  } catch (error) {
    console.error("❌ Error exporting analytics data:", error);
    return "Date,Total Requests,Unique Users,Limit Reached Count\n";
  }
};

/**
 * Lấy danh sách all users có custom limits
 * @returns {Promise<Array>}
 */
export const getCustomLimitUsers = async () => {
  try {
    const db = getDatabase();
    const customLimitsRef = ref(db, `/custom-limits`);
    const snapshot = await get(customLimitsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const customLimitsData = snapshot.val();
    const userIds = Object.keys(customLimitsData);

    // Lấy thông tin user details
    const usersRef = ref(db, `/users`);
    const usersSnapshot = await get(usersRef);

    if (!usersSnapshot.exists()) {
      return [];
    }

    const usersData = usersSnapshot.val();

    // Map data
    const customLimitUsers = userIds.map((userId) => {
      const limitData = customLimitsData[userId];
      const userData = usersData[userId];

      return {
        userId,
        displayName: userData?.profile?.displayName || "Unknown User",
        email: userData?.profile?.email || "No email",
        dailyLimit: limitData.dailyLimit,
        reason: limitData.reason,
        setBy: limitData.setBy,
        setAt: limitData.setAt,
        expiresAt: limitData.expiresAt,
        isExpired: limitData.expiresAt && limitData.expiresAt < Date.now(),
      };
    });

    // Sort by setAt descending
    customLimitUsers.sort((a, b) => b.setAt - a.setAt);

    return customLimitUsers;
  } catch (error) {
    console.error("❌ Error getting custom limit users:", error);
    return [];
  }
};

/**
 * Lấy usage distribution (phân bố users theo số lượt sử dụng)
 * @param {string} date - Format YYYY-MM-DD
 * @returns {Promise<Object>}
 */
export const getUsageDistribution = async (date) => {
  try {
    const db = getDatabase();
    const usersRef = ref(db, `/users`);
    const snapshot = await get(usersRef);

    if (!snapshot.exists()) {
      return {
        zero: 0,
        one: 0,
        two: 0,
        three: 0,
        fourToTwenty: 0,
        moreThanTwenty: 0,
      };
    }

    const usersData = snapshot.val();
    const distribution = {
      zero: 0,
      one: 0,
      two: 0,
      three: 0,
      fourToTwenty: 0,
      moreThanTwenty: 0,
    };

    Object.keys(usersData).forEach((userId) => {
      const userData = usersData[userId];

      if (userData.usageHistory && userData.usageHistory[date]) {
        const count = userData.usageHistory[date].count || 0;

        if (count === 0) distribution.zero++;
        else if (count === 1) distribution.one++;
        else if (count === 2) distribution.two++;
        else if (count >= 3 && count <= 10) distribution.three++;
        else if (count >= 11 && count <= 20) distribution.fourToTwenty++;
        else distribution.moreThanTwenty++;
      } else {
        distribution.zero++;
      }
    });

    return distribution;
  } catch (error) {
    console.error("❌ Error getting usage distribution:", error);
    return {
      zero: 0,
      one: 0,
      two: 0,
      three: 0,
      fourToTwenty: 0,
      moreThanTwenty: 0,
    };
  }
};

/**
 * Lấy hourly breakdown cho 1 ngày (usage theo từng giờ)
 * @param {string} date - Format YYYY-MM-DD
 * @returns {Promise<Array>}
 */
export const getHourlyBreakdown = async (date) => {
  try {
    const db = getDatabase();
    const usersRef = ref(db, `/users`);
    const snapshot = await get(usersRef);

    if (!snapshot.exists()) {
      return Array(24).fill(0);
    }

    const usersData = snapshot.val();
    const hourlyData = Array(24).fill(0);

    Object.keys(usersData).forEach((userId) => {
      const userData = usersData[userId];

      if (userData.usageHistory && userData.usageHistory[date]) {
        const timestamps = userData.usageHistory[date].timestamps || [];

        timestamps.forEach((timestamp) => {
          const dateObj = new Date(timestamp);
          const hour = dateObj.getHours();
          hourlyData[hour]++;
        });
      }
    });

    return hourlyData;
  } catch (error) {
    console.error("❌ Error getting hourly breakdown:", error);
    return Array(24).fill(0);
  }
};
